/**
 * Activity Log — display constants for action labels, colors, and role badges.
 */

import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";

export const ACTION_LABELS = {
  PROFILE_UPDATED: "updated project profile",
  PROFILING_STATUS_CHANGED: "changed profiling status",
  SCOPING_STATUS_CHANGED: "changed scoping status",
  MEETING_SCHEDULED: "scheduled a meeting",
  MEETING_UPDATED: "updated meeting details",
  MEETING_CANCELLED: "cancelled a meeting",
  MEETING_ATTENDEE_ADDED: "added meeting attendees",
  GAP_FILE_UPLOADED: "uploaded file to Gap Analysis",
  GAP_FILE_DELETED: "removed file from Gap Analysis",
  GAP_REPORT_AI_GENERATED: "generated gap analysis report (AI)",
  GAP_DASHBOARD_PUSHED: "pushed gap data to dashboard",
  GAP_AI_SCORES_GENERATED: "generated AI gap scores",
  GAP_QUESTIONS_AI_SELECTED: "selected gap questions (AI)",
  GAP_SPOC_RESPONSE_SUBMITTED: "submitted response to gap question",
  ORG_COMPLIANCE_REGISTER_GENERATED: "generated compliance register (AI)",
  ORG_COMPLIANCE_REGISTER_SENT_TO_SPOC: "sent compliance register to SPOC",
  ORG_COMPLIANCE_REGISTER_EMAIL_SENT: "sent compliance register email",
  ORG_COMPLIANCE_REGISTER_EMAIL_SEND_FAILED: "failed to send compliance register email",
  ORG_COMPLIANCE_REGISTER_EMAILS_BULK_SENT: "sent compliance register emails (bulk)",
  ORG_BRANDING_IMPORTED: "imported company branding",
  ORG_ARTEFACT_BRANDING_APPLIED: "applied branding to artefacts",
  ORG_ARTEFACT_FILLED: "filled artefact template",
  ORG_DELEGATE_LIST_SUBMITTED: "submitted delegate list",
  ORG_CHART_BUILT: "built org chart",
  RISK_REGISTER_AI_GENERATED: "generated risk register (AI)",
  RISK_REGISTER_SUBMITTED_FOR_REVIEW: "submitted risk register for review",
  RISK_REGISTER_SENT_TO_DELEGATE: "sent risk register to delegate",
  RISK_REGISTER_SENT_TO_SPOC: "sent risk register to SPOC",
  RISK_REGISTER_FINALIZED: "finalized risk register",
  RISK_TREATMENT_PUBLISHED: "published risk treatment plan",
  SOA_NARRATIVE_GENERATED: "generated SOA narrative (AI)",
  CONTROL_IMPL_SYNCED_FROM_SOA: "synced controls from SOA",
  CONTROL_IMPL_CHECKLIST_TOGGLED: "toggled checklist item",
  CONTROL_IMPL_STAGE_SUBMITTED: "submitted control stage",
  CONTROL_IMPL_STAGE_ADVANCED: "advanced control to next stage",
  CONTROL_IMPL_CUSTOM_ITEM_ADDED: "added custom checklist item",
  CONTROL_IMPL_CHECKLIST_ITEM_REMOVED: "removed checklist item",
  CONTROL_IMPL_ATTACHMENT_UPLOADED: "uploaded control attachment",
  CONTROL_IMPL_ATTACHMENT_REMOVED: "removed control attachment",
  DOCUMENT_LIBRARY_ENTRY_ADDED: "added document to library",
  EVIDENCE_AI_SCORED: "scored evidence register (AI)",
  EVIDENCE_WORKFLOW_TRANSITIONED: "transitioned evidence workflow",
  INCIDENT_CREATED: "created an incident",
  INCIDENT_UPDATED: "updated incident",
  INCIDENT_STATUS_CHANGED: "changed incident status",
  INTERNAL_AUDIT_PLAN_ROW_UPDATED: "updated audit plan row",
  INTERNAL_AUDIT_PLAN_CIRCULATED: "circulated audit plan",
  INTERNAL_AUDIT_QUESTIONNAIRE_GENERATED: "generated audit questionnaire (AI)",
  INTERNAL_AUDIT_FINDINGS_GENERATED: "generated audit findings",
  INTERNAL_AUDIT_CAP_GENERATED: "generated corrective actions",
  INTERNAL_AUDIT_REPORT_GENERATED: "generated audit report (AI)",
  INTERNAL_AUDIT_QUESTION_ANSWERED: "answered audit question",
  INTERNAL_AUDIT_FINDING_EDITED: "edited audit finding",
  INTERNAL_AUDIT_CAP_UPDATED: "updated corrective action",
  INTERNAL_AUDIT_REPORT_DOWNLOADED: "downloaded audit report",
  MILESTONES_AI_GENERATED: "generated milestones (AI)",
  COMPANY_AI_PROFILED: "profiled company (AI)",
  PROJECT_CREATED: "created project",
  PROJECT_DELETED: "deleted project",
  COMPANY_CREATED: "created company",
  USER_INVITED: "invited user",
  ROLE_ASSIGNED: "assigned role",
  QUESTIONBANK_CSV_UPLOADED: "uploaded question bank CSV",
  ISO_POLICY_HUB_CSV_UPLOADED: "uploaded ISO policy hub CSV",
};

export const ACTION_CATEGORIES = {
  ai: {
    color: "purple",
    dotClass: "bg-[color:var(--color-accent)]",
    bgClass: "bg-[color:var(--color-info-bg)]",
    textClass: "text-[color:var(--color-accent)]",
    borderClass: "border-[color:var(--color-info-line)]",
    // "_AI_" (underscore-bounded), not bare "AI": a bare "AI" substring collides with
    // "EMAIL" (E-M-AI-L) and would misclassify every compliance-register EMAIL_* action
    // as an AI event instead of its correct delete/review category (#166).
    //
    // Verification set for the "zero classification changes" claim in the #166 commit
    // was the 63 pre-existing ACTION_LABELS keys — NOT the full backend action
    // population. getActionCategory runs on every action string the backend logs,
    // labeled or not, which is the whole reason #166 existed (the three new
    // compliance-email actions weren't labeled either). Re-derived the real
    // population directly from policylens-backend (`git grep -E "action:\s*['"]
    // [A-Z0-9_]+['"]"`) on 2026-09-11: 105 distinct action literals, 17 containing a
    // bare "AI" substring. Of those 17, this tightening reclassifies 4 pre-existing,
    // unlabeled, live actions away from purple "ai" (previously misclassified, same
    // bug as EMAIL) to a different (still wrong) category:
    //   - AUTH_FAILURE (src/middleware/auth.ts) -> falls through to the "edit" gray
    //     default (no other keyword matches).
    //   - QUESTION_CREATION_FAILED (src/controllers/admin.controller.ts) -> same
    //     "edit" gray default.
    //   - FRAMEWORK_CSV_UPLOAD_FAILED (src/controllers/admin.controller.ts) -> matches
    //     "upload" (blue) via its own "UPLOAD" substring, checked before "delete" in
    //     iteration order.
    //   - QUESTIONBANK_CSV_UPLOAD_FAILED (src/controllers/admin.controller.ts) -> same
    //     "upload" blue match.
    // These 4 are not regressions introduced by this tightening — they were already
    // miscoloured (purple "ai" for a failure) before it. This change trades one wrong
    // colour for another; it does not newly break anything. Proper severity treatment
    // (e.g. a dedicated "failure" category, or reordering categories so FAILED-type
    // actions outrank UPLOAD/etc.) is deliberately NOT done here — see follow-up below.
    //
    // "_AI_" is a both-sides-bounded heuristic, not a fully robust one: it has zero
    // false negatives against today's real 105-action population (nothing in that
    // population starts with "AI_" or ends with "_AI"), but a future action named e.g.
    // AI_DRAFT_CREATED or DRAFT_CREATED_AI would silently fail to match and fall
    // through uncategorized as "ai". Re-check this keyword if a new AI-related action
    // is ever added at a word boundary.
    keywords: ["_AI_", "GENERATED", "SCORED", "PROFILED", "AI_SELECT"],
  },
  upload: {
    color: "blue",
    dotClass: "bg-[color:var(--color-accent)]",
    bgClass: "bg-[color:var(--color-info-bg)]",
    textClass: "text-[color:var(--color-accent)]",
    borderClass: "border-[color:var(--color-info-line)]",
    keywords: ["UPLOADED", "UPLOAD", "FILE", "ATTACHMENT_UPLOADED", "CSV_UPLOADED"],
  },
  status: {
    color: "green",
    dotClass: "bg-[color:var(--color-ok-fg)]",
    bgClass: "bg-[color:var(--color-ok-bg)]",
    textClass: "text-[color:var(--color-ok-fg)]",
    borderClass: "border-[color:var(--color-ok-line)]",
    keywords: ["STATUS", "CIRCULATED", "FINALIZED", "PUBLISHED", "ADVANCED", "TRANSITIONED", "PUSHED"],
  },
  edit: {
    color: "slate",
    dotClass: "bg-[color:var(--color-text-muted)]",
    bgClass: "bg-[color:var(--color-surface-sunken)]",
    textClass: "text-[color:var(--color-text-muted-dark)]",
    borderClass: "border-[color:var(--color-border)]",
    keywords: ["UPDATED", "EDITED", "TOGGLED", "ANSWERED", "PROFILE_UPDATED"],
  },
  delete: {
    color: "red",
    dotClass: "bg-[color:var(--color-danger-fg)]",
    bgClass: "bg-[color:var(--color-danger-bg)]",
    textClass: "text-[color:var(--color-danger-fg)]",
    borderClass: "border-[color:var(--color-danger-line)]",
    keywords: ["DELETED", "REMOVED", "CANCELLED", "SEND_FAILED"],
  },
  review: {
    color: "amber",
    dotClass: "bg-[color:var(--color-warn-fg)]",
    bgClass: "bg-[color:var(--color-warn-bg)]",
    textClass: "text-[color:var(--color-warn-fg)]",
    borderClass: "border-[color:var(--color-warn-line)]",
    keywords: ["REVIEW", "SUBMITTED", "SENT_TO", "APPROVED", "INVITED", "ASSIGNED", "EMAIL_SENT", "BULK_SENT"],
  },
  meeting: {
    color: "teal",
    dotClass: "bg-[color:var(--color-accent)]",
    bgClass: "bg-[color:var(--color-info-bg)]",
    textClass: "text-[color:var(--color-accent)]",
    borderClass: "border-[color:var(--color-info-line)]",
    keywords: ["MEETING", "SCHEDULED", "ATTENDEE"],
  },
};

// FOLLOW-UP (filed, not fixed here — see #166 commit and ai.keywords comment above):
// AUTH_FAILURE, QUESTION_CREATION_FAILED, FRAMEWORK_CSV_UPLOAD_FAILED, and
// QUESTIONBANK_CSV_UPLOAD_FAILED are unlabeled, pre-existing actions that render
// with the wrong severity colour (gray "edit" or blue "upload", the same as their
// own successful/routine siblings) instead of a red/negative-signal colour. Two of
// them (AUTH_FAILURE, QUESTION_CREATION_FAILED) are exactly the failure-invisibility
// bug class #166 was created to fix, just for a different action family.
//
// Not a one-line fix: getActionCategory returns on first keyword match, iterating
// ACTION_CATEGORIES in declared order (ai, upload, status, edit, delete, review,
// meeting). Adding a bare "FAILED" keyword to `delete` fixes AUTH_FAILURE and
// QUESTION_CREATION_FAILED (they match no earlier category) but does NOT fix the two
// CSV_UPLOAD_FAILED actions — they contain "UPLOAD", which is checked earlier in
// iteration order and wins first. A real fix needs either (a) an explicit
// action-name -> category override map for these exceptions, or (b) reordering
// categories / adding a dedicated "failure" category — and either must be
// re-verified against the full backend action population (105 actions as of
// 2026-09-11, re-derive via `git grep -E "action:\s*['"][A-Z0-9_]+['"]"` in
// policylens-backend), not just the labeled subset, or it risks silently
// reclassifying unrelated UPLOADED/DELETED/etc. actions the same way #166 did.
// Deferred because introducing a new severity category (vs. reusing "delete" for
// auth/CSV failures, which is itself a stretch) is a product decision, not a
// mechanical one.
export function getActionCategory(action) {
  for (const [, cat] of Object.entries(ACTION_CATEGORIES)) {
    if (cat.keywords.some((kw) => action?.includes(kw))) return cat;
  }
  return ACTION_CATEGORIES.edit;
}

export function getActionLabel(action) {
  return ACTION_LABELS[action] || action?.toLowerCase().replace(/_/g, " ") || "performed an action";
}

export const ROLE_BADGES = {
  lead_advisor: { label: "LA", color: "bg-[color:var(--color-info-bg)] text-[color:var(--color-info-fg)]" },
  advisor: { label: "AD", color: "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]" },
  company_spoc: { label: "SP", color: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)]" },
  company_delegate: { label: "DL", color: "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]" },
  platform_admin: { label: "PA", color: "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)]" },
};

export function getRoleBadge(role) {
  const key = (role || "").toLowerCase().replace(/-/g, "_");
  return ROLE_BADGES[key] || { label: key.slice(0, 2).toUpperCase(), color: "bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]" };
}

export function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDdMmYyyy(dateStr);
}

export function groupEventsByDate(events) {
  const groups = [];
  let currentLabel = null;
  let currentItems = [];
  // Day buckets are IST calendar days (Commandment XII), not the browser's.
  const today = formatDdMmYyyy(Date.now());
  const yesterday = formatDdMmYyyy(Date.now() - 86400000);

  for (const ev of events) {
    const evDate = formatDdMmYyyy(ev.timestamp);
    const label = evDate === today ? "Today" : evDate === yesterday ? "Yesterday" : evDate;
    if (label !== currentLabel) {
      if (currentLabel !== null) groups.push({ label: currentLabel, items: currentItems });
      currentLabel = label;
      currentItems = [];
    }
    currentItems.push(ev);
  }
  if (currentLabel !== null) groups.push({ label: currentLabel, items: currentItems });
  return groups;
}
